import { type RefObject, useCallback, useEffect, useState } from 'react'
import type { CanvasPosition } from '../../../../types/infrastructure-map'

interface UseTileDragArgs {
  nodeRef: RefObject<HTMLDivElement>
  scale: number
  position: CanvasPosition
  onDrag: (position: CanvasPosition) => void
  onDragEnd: (position: CanvasPosition) => void
}

export interface TileDragState {
  isDragging: boolean
  handleMouseDown: (event: React.MouseEvent) => void
}

export function useTileDrag({
  nodeRef,
  scale,
  position,
  onDrag,
  onDragEnd,
}: UseTileDragArgs): TileDragState {
  const [isDragging, setIsDragging] = useState(false)
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 })

  const handleMouseDown = (event: React.MouseEvent): void => {
    if (event.button !== 0) return
    event.preventDefault()
    event.stopPropagation()
    const rect = nodeRef.current?.getBoundingClientRect()
    if (!rect) return
    setDragOffset({
      x: (event.clientX - rect.left - rect.width / 2) / scale,
      y: (event.clientY - rect.top - rect.height / 2) / scale,
    })
    setIsDragging(true)
  }

  const handleMouseMove = useCallback(
    (event: MouseEvent) => {
      if (!isDragging || !nodeRef.current?.parentElement) return
      const parentRect = nodeRef.current.parentElement.getBoundingClientRect()
      const newX = (event.clientX - parentRect.left) / scale - dragOffset.x
      const newY = (event.clientY - parentRect.top) / scale - dragOffset.y
      onDrag({ x: newX, y: newY })
    },
    [isDragging, dragOffset, scale, onDrag, nodeRef]
  )

  const handleMouseUp = useCallback(() => {
    if (isDragging) {
      setIsDragging(false)
      onDragEnd(position)
    }
  }, [isDragging, position, onDragEnd])

  useEffect(() => {
    if (!isDragging) return
    window.addEventListener('mousemove', handleMouseMove)
    window.addEventListener('mouseup', handleMouseUp)
    return () => {
      window.removeEventListener('mousemove', handleMouseMove)
      window.removeEventListener('mouseup', handleMouseUp)
    }
  }, [isDragging, handleMouseMove, handleMouseUp])

  return { isDragging, handleMouseDown }
}
